import { ExportResult, JobResultResponse } from '@upright/common/internal/jobs/types';
import { GroupfolioEntityType } from '@upright/common/model/input/groupfolios/types';
import { responseSchemaFor } from '@upright/common/zod-utils';
import { z } from 'zod';
import { ModelEntityReference } from '../../../input/types';
import { zImpactScoresByTIVV, zNetScoresByType } from '../types';
import { ModelEntityWithScores, ResultContextWithInfo, WeightableModelEntitiesByWeightSetId } from './types';
import { LoadableModelEntityReference, LoadableModelEntityType } from './types/types-entity-with-result-common';
import { WeightableEntityWithResult } from './types/types-weightable';

export interface GetModelEntityDataInBulkPayload {
  entities: LoadableModelEntityReference[];
  version?: string;
  release?: string | null;
}

export interface GetModelEntityDataParams {
  versionOrRelease: string;
  type: LoadableModelEntityType;
  id: string;
}

/**
 * Response guard for the model entity endpoints, checked at the fetch boundary (see
 * `fetchAndValidateOrThrow`). `ModelEntityWithScores` is an intersection of several legacy
 * result interfaces with most fields optional, so this describes only what the score readers
 * depend on: an entity identity, and results whose score maps hold numbers.
 *
 * `type` is a string rather than the `ModelEntityType` enum because the endpoint answers a
 * variant reference with `type: 'companyVariant'`, which the response type does not admit.
 *
 * The score maps are nullish-tolerant: a result the viewer has no output access to, or a
 * placeholder result (`SpecialModelResultId.NO_SCORES`), carries them empty or not at all.
 */
const zModelEntityWithScoresSchema = z.object({
  id: z.string(),
  type: z.string(),
  modelResults: z.array(
    z.object({
      /**
       * Deliberately wider than the declared `number`: company and companyVariant results carry
       * the `company_variant.id` UUID here (`variantToCompanyResult` in
       * `services/main/common/model/variant-adapter`), and the response type only says `number`
       * because the handler narrows it with an explicit `resultId as number // TEMPORARY` cast
       * (`upserve/modules/scores/model-entity-data/fetch-model-entities`) — `ScoreBase.resultId`
       * is `number | string` one layer below. Readers already treat it as a string
       * (`use-impact-topics-query` and siblings). Groups, portfolios and the placeholder ids
       * (`SpecialModelResultId`) are numeric. Making the type honest is PLT-6185; until then the
       * schema has to accept what the servers send, so `SchemaAccepts` cannot police this field.
       */
      resultId: z.union([z.number(), z.string()]),
      netScoresByType: zNetScoresByType.nullish(),
      impactScoresByTIVV: zImpactScoresByTIVV.nullish(),
    }),
  ),
});
export const zModelEntityWithScores = responseSchemaFor<ModelEntityWithScores, typeof zModelEntityWithScoresSchema>(
  zModelEntityWithScoresSchema,
);

const zGetModelEntityDataInBulkResponseSchema = z.array(zModelEntityWithScoresSchema);
export const zGetModelEntityDataInBulkResponse = responseSchemaFor<
  ModelEntityWithScores[],
  typeof zGetModelEntityDataInBulkResponseSchema
>(zGetModelEntityDataInBulkResponseSchema);

export interface GetWeightSetItemDataRequest {
  entityRef: ModelEntityReference;
  weightSetId?: number;
}

export interface GetWeightSetItemDataResponse {
  weightSetId: number;
  itemData: WeightableEntityWithResult[];
}

export type GetItemsForWeightSetResponse = WeightableEntityWithResult[];

export interface TriggerResultExportResponse {
  jobId: string; // Hashed
}

export type PollExportResponse = JobResultResponse<ExportResult>;

// TODO ML: Not used in UI anymore.
export type GetItemsForAllWeightSetsResponse = WeightableModelEntitiesByWeightSetId;

export interface ApiVariantKey {
  scenarioId?: string;
  startDate?: string;
  endDate?: string;
}
export interface FetchModelEntityDataInBulkOptions extends ApiVariantKey {
  includeRating?: boolean;
}

export type FetchSingleModelEntityDataOptions = ApiVariantKey & GroupFolioOptions;

type GroupFolioOptions = {
  weightSetResultContext?: string;
};

export type GetResultContextsRequestParams = {
  entityId: string;
  entityType: GroupfolioEntityType;
  release: string;
};
export type GetResultContextsResponse = ResultContextWithInfo[];
